import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { resolvePathWithBase } from '@/utils/urlBase';

import useHirePortContext from './useHirePortContext';

import type { Location, NavigateOptions, To } from 'react-router';

type SetParamValue = string | number | boolean | null | undefined;

/**
 * Legacy `history`-shaped surface backed by react-router v8 navigation.
 *
 * `location` is exposed as a live getter so the object identity stays stable
 * across navigations (mirroring the v5 history singleton). This keeps effects
 * that depend on `history` from re-running on every location change, which is
 * what prevents the "Maximum update depth" loops seen right after the upgrade.
 */
export type LegacyHistory = {
  readonly location: Location;
  push: (to: To | string, state?: unknown) => void;
  replace: (to: To | string, state?: unknown) => void;
  goBack: () => void;
  go: (delta: number) => void;
};

/** urlBase-aware path navigation for API redirect helpers — pass `navigate` from `useUrlState()`. */
export type AppNavigate = (path: string) => void;

/**
 * Single unified hook for page navigation + URL search-param state.
 *
 * Replaces the scattered `useHistory` + manual `URLSearchParams` usage. All
 * navigation is `urlBase`-aware (embed prefix) and absolute URLs
 * trigger a hard redirect, matching the previous `useHistoryWithUrlBase`.
 */
export function useUrlState() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { urlBase } = useHirePortContext();

  // Read inside callbacks via a ref so navigation helpers keep a stable identity.
  const locationRef = useRef(location);
  // Sync on every render so getParam/history.location match location.search during render.
  // See docs/agent-ui-patterns.md § URL state (react-hooks/refs is intentional here).
  // eslint-disable-next-line react-hooks/refs -- ref mirrors location for stable callback identity
  locationRef.current = location;

  const resolve = useCallback(
    (path: string) => resolvePathWithBase(path, urlBase),
    [urlBase],
  );

  const go = useCallback(
    (to: To | string | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        void navigate(to);
        return;
      }
      if (typeof to === 'string') {
        if (to.startsWith('http:') || to.startsWith('https:')) {
          if (typeof window !== 'undefined') window.location.href = to;
          return;
        }
        void navigate(resolve(to), options);
        return;
      }
      void navigate(to, options);
    },
    [navigate, resolve],
  );

  const goBack = useCallback(() => void navigate(-1), [navigate]);

  const getParam = useCallback(
    (key: string) => new URLSearchParams(locationRef.current.search).get(key),
    [],
  );

  const getSearchParams = useCallback(
    () => new URLSearchParams(locationRef.current.search),
    [],
  );

  /**
   * Merge updates into the current search string and navigate (defaults to
   * `replace` so filter/tab changes don't spam the history stack). Foreign
   * params (e.g. `?lang=`) are preserved; empty/null values are removed.
   */
  const setParams = useCallback(
    (
      updates: Record<string, SetParamValue>,
      options?: { replace?: boolean },
    ) => {
      const sp = new URLSearchParams(locationRef.current.search);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          sp.delete(key);
        } else {
          sp.set(key, String(value));
        }
      });
      const search = sp.toString();
      void navigate(
        {
          pathname: locationRef.current.pathname,
          search: search ? `?${search}` : '',
        },
        { replace: options?.replace ?? true },
      );
    },
    [navigate],
  );

  const history = useMemo<LegacyHistory>(
    () => ({
      get location() {
        return locationRef.current;
      },
      push: (to, state) => go(to, state !== undefined ? { state } : undefined),
      replace: (to, state) =>
        go(to, { replace: true, ...(state !== undefined ? { state } : {}) }),
      goBack: () => void navigate(-1),
      go: (delta: number) => void navigate(delta),
    }),
    // location is read via ref; deps kept minimal so identity stays stable

    [go, navigate],
  );

  return {
    /** urlBase-aware navigate. Accepts a path string, `To` object, or history delta. */
    navigate: go,
    /**
     * Pass to API helpers for resource_not_found redirects.
     * Same as `navigate` — urlBase keeps embed/plugin on pack-scoped `/not-found`.
     */
    navigateOnResourceNotFound: go,
    goBack,
    location,
    params,
    getParam,
    getSearchParams,
    setParams,
    /** Legacy history-shaped surface for incremental migration. */
    history,
  };
}
